import axios from 'axios';

// URL del API - usa variable de entorno o fallback a /api
const API_URL =
  window.__API_BASE_URL__ ||
  localStorage.getItem('api_base_url') ||
  process.env.REACT_APP_API_URL ||
  `${window.location.origin}/api`;

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 segundos de timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Error de red o timeout
    if (!error.response) {
      console.error('Error de conexión:', error.message);
      error.message = 'Error de conexión con el servidor. Verifique su conexión a internet.';
      return Promise.reject(error);
    }
    
    // Token expirado o no válido
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Solo redirigir si no estamos ya en login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    // Error del servidor
    if (error.response?.status >= 500) {
      console.error('Error del servidor:', error.response?.data?.message || error.message);
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => apiClient.post('/login.php', credentials),
  verifyToken: () => apiClient.get('/login.php'),
};

export const productosAPI = {
  getAll: () => apiClient.get('/productos.php'),
  getById: (id) => apiClient.get(`/productos.php?id=${id}`),
  getByCodigo: (codigo) => apiClient.get(`/productos.php?codigo_barras=${codigo}`),
  search: (query) => apiClient.get(`/productos.php?search=${query}`),
  create: (data) => apiClient.post('/productos.php', data),
  update: (id, data) => apiClient.put('/productos.php', { ...data, id }),
  delete: (id) => apiClient.delete('/productos.php', { data: { id } }),
};

export const ventasAPI = {
  getAll: (params) => apiClient.get('/ventas.php', { params }),
  getById: (id) => apiClient.get(`/ventas.php?id=${id}`),
  create: (data) => apiClient.post('/ventas.php', data),
  getByFecha: (fecha) => apiClient.get('/ventas.php', { params: { fecha_inicio: fecha, fecha_fin: fecha } }),
  search: (query) => apiClient.get(`/ventas.php?search=${query}`),
  cancel: (id) => apiClient.put('/ventas.php', { id, action: 'cancel' }),
};

export const clientesAPI = {
  getAll: () => apiClient.get('/clientes.php'),
  getById: (id) => apiClient.get(`/clientes.php?id=${id}`),
  search: (query) => apiClient.get(`/clientes.php?search=${query}`),
  create: (data) => apiClient.post('/clientes.php', data),
  update: (data) => apiClient.put('/clientes.php', data),
  delete: (id) => apiClient.delete('/clientes.php', { data: { id } }),
};

export const proveedoresAPI = {
  getAll: () => apiClient.get('/proveedores.php'),
  getById: (id) => apiClient.get(`/proveedores.php?id=${id}`),
  create: (data) => apiClient.post('/proveedores.php', data),
  update: (data) => apiClient.put('/proveedores.php', data),
  delete: (id) => apiClient.delete('/proveedores.php', { data: { id } }),
};

export const comprasAPI = {
  getAll: (params) => apiClient.get('/compras.php', { params }),
  getById: (id) => apiClient.get(`/compras.php?id=${id}`),
  create: (data) => apiClient.post('/compras.php', data),
  update: (data) => apiClient.put('/compras.php', data),
};

export const cajaAPI = {
  abrir: (data) => apiClient.post('/caja.php', { action: 'abrir', ...data }),
  cerrar: (cajaId, data) => apiClient.post('/caja.php', { action: 'cerrar', caja_id: cajaId, ...data }),
  getActual: () => apiClient.get('/caja.php?action=actual'),
  getMovimientos: (cajaId) => apiClient.get(`/caja.php?action=movimientos&caja_id=${cajaId}`),
  getHistorial: (params) => apiClient.get('/caja.php?action=historial', { params }),
  registrarMovimiento: (data) => apiClient.post('/caja.php', { action: 'movimiento', ...data }),
};

export const reportesAPI = {
  ventasPorPeriodo: (params) => apiClient.get('/reportes.php?tipo=ventas', { params }),
  productosMasVendidos: (params) => apiClient.get('/reportes.php?tipo=productos', { params }),
  utilidades: (params) => apiClient.get('/reportes.php?tipo=utilidades', { params }),
  inventario: () => apiClient.get('/reportes.php?tipo=inventario'),
  cuentasPorCobrar: () => apiClient.get('/reportes.php?tipo=cuentas_cobrar'),
  dashboard: () => apiClient.get('/reportes.php?tipo=dashboard'),
};

export const configAPI = {
  get: () => apiClient.get('/configuracion.php'),
  update: (data) => apiClient.put('/configuracion.php', data),
  getSucursales: () => apiClient.get('/configuracion.php?tipo=sucursales'),
  getAlmacenes: () => apiClient.get('/configuracion.php?tipo=almacenes'),
};

export const creditosAPI = {
  getAll: (estado) => apiClient.get(`/creditos.php${estado ? `?estado=${estado}` : ''}`),
  getById: (id) => apiClient.get(`/creditos.php?id=${id}`),
  registrarPago: (id, monto_pago) => apiClient.put('/creditos.php', { id, monto_pago }),
  getResumenClientes: () => apiClient.get('/creditos.php?resumen=1'),
  getEstadoCuentaCliente: (clienteId) => apiClient.get(`/creditos.php?cliente_id=${clienteId}`),
  registrarAbonoCliente: (clienteId, monto_pago, extra = {}) =>
    apiClient.put('/creditos.php', { cliente_id: clienteId, monto_pago, ...extra }),
};

export const cotizacionesAPI = {
  getAll: () => apiClient.get('/cotizaciones.php'),
  getById: (id) => apiClient.get(`/cotizaciones.php?id=${id}`),
  create: (data) => apiClient.post('/cotizaciones.php', data),
  update: (id, data) => apiClient.put('/cotizaciones.php', { id, ...data }),
  updateEstado: (id, estado) => apiClient.put('/cotizaciones.php', { id, estado }),
  delete: (id) => apiClient.delete('/cotizaciones.php', { data: { id } }),
};

export const backupAPI = {
  download: () => apiClient.get('/backup.php', { responseType: 'blob' }),
};

export const usuariosAPI = {
  getAll: () => apiClient.get('/usuarios.php'),
  getById: (id) => apiClient.get(`/usuarios.php?id=${id}`),
  getPermisos: (userId) => apiClient.get(`/usuarios.php?permisos=${userId}`),
  updatePermisos: (userId, permisos) => apiClient.post('/usuarios.php', { action: 'updatePermisos', user_id: userId, permisos }),
  create: (data) => apiClient.post('/usuarios.php', data),
  update: (data) => apiClient.put('/usuarios.php', data),
  delete: (id) => apiClient.delete('/usuarios.php', { data: { id } }),
};

export const permisosAPI = {
  getModulos: () => apiClient.get('/permisos.php?modulos=1'),
  getAll: () => apiClient.get('/permisos.php'),
  asignar: (data) => apiClient.post('/permisos.php', data),
};

export const rolesAPI = {
  getAll: () => apiClient.get('/roles.php'),
  getById: (id) => apiClient.get(`/roles.php?id=${id}`),
  getPermisos: () => apiClient.get('/roles.php?permisos=all'),
  create: (data) => apiClient.post('/roles.php', data),
  update: (data) => apiClient.put('/roles.php', data),
  delete: (id) => apiClient.delete('/roles.php', { data: { id } }),
};

export const mayoristaAPI = {
  getAll: (params) => apiClient.get('/ventas_mayorista.php', { params }),
  getById: (id) => apiClient.get(`/ventas_mayorista.php?id=${id}`),
  getProductos: () => apiClient.get('/ventas_mayorista.php?productos=1'),
  create: (data) => apiClient.post('/ventas_mayorista.php', data),
  update: (data) => apiClient.put('/ventas_mayorista.php', data),
  cancel: (id) => apiClient.delete('/ventas_mayorista.php', { data: { id } }),
};

export const sucursalesAPI = {
  getAll: () => apiClient.get('/configuracion.php?tipo=sucursales'),
};

export default apiClient;
